// Split out of wheelUtils so a component can take a wheel colour without pulling in d3, which is untransformed ESM under ts-jest.
import { WheelNode } from '../../types/geneCodes/geneticWheel';

/**
 * Determines color for a wheel node based on its type and risk level
 * Updated to match the old JavaScript system exactly
 */
export const getNodeColor = (node: WheelNode, isDarkMode: boolean): string => {
	// Use exact colors from the old JavaScript system
	const OLD_SYSTEM_COLORS = {
		red: '#dc7699',     // var_red from old system
		orange: '#dcc94c',  // var_orange from old system  
		green: '#3ed3a3',   // var_green from old system
		grey: '#d9d9d9'     // grey from old system
	};

	// R8: cool and darker so it cannot be mistaken for the warm neutral grey a no-call takes, nor for green.
	const UNGENOTYPED_COLOR = '#8fa3b8';

	// Risk level based colors (matches old system exactly)
	if (node.riskLevel === 'bad') return OLD_SYSTEM_COLORS.red;
	if (node.riskLevel === 'medium') return OLD_SYSTEM_COLORS.orange;
	if (node.riskLevel === 'good') return OLD_SYSTEM_COLORS.green;
	// Never genotyped for this sample — an absent result, not a no-risk one.
	if (node.riskLevel === 'ungenotyped') return UNGENOTYPED_COLOR;
	if (node.riskLevel === null) return OLD_SYSTEM_COLORS.grey; // Grey for missing/no data

	// Default colors by type when no risk level is available
	if (node.type === 'trait') return '#5470c6';
	if (node.type === 'gene') return '#91cc75';
	if (node.type === 'snp') return '#fac858';

	return isDarkMode ? "#000" : '#fff'; // Default fallback
};

/** Perceived brightness of a `#rgb`/`#rrggbb` surface; anything else is treated as light. */
const isDarkSurface = (value: string): boolean => {
	const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((value ?? '').trim())?.[1];
	if (!hex) return false;

	const full = hex.length === 3 ? hex.replace(/./g, char => char + char) : hex;
	const [r, g, b] = [0, 2, 4].map(offset => Number.parseInt(full.slice(offset, offset + 2), 16));

	return r * 0.299 + g * 0.587 + b * 0.114 < 128;
};

// Design-system §9: the theme is the class on <html>. The antd surface is only the fallback for a tree
// mounted without one, and is read by brightness rather than against a literal value.
export const isDarkTheme = (surfaceColor?: string): boolean => {
	const classes = typeof document === 'undefined' ? null : document.documentElement.classList;
	if (classes?.contains('light-theme')) return false;
	if (classes?.contains('dark-theme')) return true;
	return isDarkSurface(surfaceColor ?? '');
};
